/* eslint-disable max-len */
// @flow
import { omit } from 'ramda';
import request, { apiURL, del, get, post } from '../utils/api';
import { getCookie } from '../utils/cookie';

type StartTransferEligibility = {
  enabled: boolean,
  disabledReasons: Array<string>,
};

type DraftUnitTransferResponse = {
  id: string,
};

type HouseholdMemberCommon = {
  id: string,
  applicantId: string,
  applicant: {
    id: string,
    applicantTypeId: string,
    applicantType: {
      name: string,
      financiallyResponsible: boolean,
    },
    affordableRelationshipId: string,
  },
  rc: any,
  residentMinor: any,
  residentPet: any,
  relationshipId: string,
  relationship: Object,
  status: string,
  startDate: Date,
  endDate: Date,
  checklist: any,
};

export type AdultHouseholdMember = {
  type: 'adult',
  rc: {
    id: string,
    customerId: string,
    customer: {
      firstName: string,
      middleName?: string,
      lastName: string,
      preferredName?: string,
      suffixId?: string,
      suffix: Object,
      phoneNumber: string,
      emailAddress?: string,
      parentCompany?: string,
    },
    relationshipId: string,
    isPrimary: boolean,
  },
} & HouseholdMemberCommon;

export type MinorHouseholdMember = {
  type: 'minor',
  residentMinor: {
    firstName: string,
    middleName?: string,
    lastName: string,
    preferredName?: string,
    suffixId?: string,
    suffix: Object,
    age?: string,
    dateOfBirth?: Date,
  },
} & HouseholdMemberCommon;

export type PetHouseholdMember = {
  type: 'pet',
  residentPet: {
    name: string,
    breedId: string,
    petTypeId: string,
    petType: Object,
    weight?: number,
    petTag?: string,
    isServiceAnimal?: boolean,
  },
} & HouseholdMemberCommon;

export type HouseholdMember =
  | AdultHouseholdMember
  | MinorHouseholdMember
  | PetHouseholdMember;

export default class ResidentService {
  getResident(
    organizationId: string,
    propertyId: string,
    residentId: string,
    includeSoftDeletedUnits: boolean = false,
  ): any {
    const options = {
      method: 'GET',
      headers: {
        Authorization: getCookie('session_id'),
        'Content-Type': 'application/json',
      },
    };

    return request(
      `${apiURL}/${organizationId}/${propertyId}/resident/${residentId}${
        includeSoftDeletedUnits ? '?includeSoftDeletedUnits=true' : ''
      }`,
      options,
    );
  }

  moveOut(
    organizationId: string,
    propertyId: string,
    residentId: string,
    moveOutData: Object,
  ): any {
    const options = {
      method: 'PUT',
      headers: {
        Authorization: getCookie('session_id'),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(moveOutData),
    };

    return request(
      `${apiURL}/${organizationId}/${propertyId}/resident/${residentId}/move-out`,
      options,
    );
  }
  saveMonthlyChargeResident(
    organizationId: string,
    propertyId: string,
    leaseId: string,
    transaction: string,
  ): any {
    const options = {
      method: 'POST',
      headers: {
        Authorization: getCookie('session_id'),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(transaction),
    };
    return request(
      `${apiURL}/${organizationId}/${propertyId}/lease/${leaseId}/leaseMonthlyTransactionResident`,
      options,
    );
  }

  getMonthlyTransactionsResident(leaseId: string): any {
    const options = {
      method: 'GET',
      headers: {
        Authorization: getCookie('session_id'),
        'Content-Type': 'application/json',
      },
    };

    return request(
      `${apiURL}/lease/${leaseId}/leaseMonthlyTransactionResident`,
      options,
    );
  }

  renewLease(previousLeaseId: string, lease: any): any {
    const options = {
      method: 'POST',
      headers: {
        Authorization: getCookie('session_id'),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(lease),
    };
    return request(
      `${apiURL}/previousLease/${previousLeaseId}/lease-renewal-save`,
      options,
    );
  }

  startTransferLease(
    organizationId: string,
    propertyId: string,
    residentId: string,
    applicationId: string,
    unitId: string,
    userId: string,
  ): any {
    const options = {
      method: 'POST',
      headers: {
        Authorization: getCookie('session_id'),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ residentId, applicationId, userId, unitId }),
    };
    return request(
      `${apiURL}/${organizationId}/${propertyId}/household/start-transfer`,
      options,
    );
  }
  completeTransfer(
    organizationId: string,
    propertyId: string,
    actualTransferDate: string,
    leaseId: string,
    userId: string,
  ): any {
    const options = {
      method: 'POST',
      headers: {
        Authorization: getCookie('session_id'),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ actualTransferDate, userId, leaseId }),
    };
    return request(
      `${apiURL}/${organizationId}/${propertyId}/household/complete-transfer`,
      options,
    );
  }

  getTransferRequirements(
    organizationId: string,
    propertyId: string,
    leaseId: string,
    userId: string,
  ): any {
    const options = {
      method: 'GET',
      headers: {
        Authorization: getCookie('session_id'),
        'Content-Type': 'application/json',
      },
    };
    return request(
      `${apiURL}/${organizationId}/${propertyId}/lease/${leaseId}/transfer-requirements`,
      options,
    );
  }

  getStartTransferEligibility(
    organizationId: string,
    propertyId: string,
    householdId: string,
  ): Promise<StartTransferEligibility> {
    const options = {
      method: 'GET',
      headers: {
        Authorization: getCookie('session_id'),
        'Content-Type': 'application/json',
      },
    };
    return request(
      `${apiURL}/${organizationId}/${propertyId}/household/${householdId}/start-transfer-eligibility`,
      options,
    );
  }

  createDraftUnitTransfer(
    organizationId: string,
    propertyId: string,
    householdId: string,
    payload: { transferDate: string, transferReasonId: string },
  ): Promise<DraftUnitTransferResponse> {
    const options = {
      method: 'POST',
      headers: {
        Authorization: getCookie('session_id'),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    };
    return request(
      `${apiURL}/${organizationId}/${propertyId}/household/${householdId}/unit-transfer/draft`,
      options,
    );
  }

  // ENG-30316: the Manage Units screen compares this date against each unit's prior household
  // move-out date to decide whether Assign is available — a unit cannot be taken until the day
  // after it is vacated. Resolves to null when the household has no draft transfer.
  getDraftUnitTransfer(
    organizationId: string,
    propertyId: string,
    householdId: string,
  ): Promise<mixed> {
    const options = {
      method: 'GET',
      headers: {
        Authorization: getCookie('session_id'),
        'Content-Type': 'application/json',
      },
    };
    return request(
      `${apiURL}/${organizationId}/${propertyId}/household/${householdId}/unit-transfer/draft`,
      options,
    );
  }

  completeUnitTransferSetup(
    organizationId: string,
    propertyId: string,
    householdId: string,
    payload: { applicationId: string, unitId: string },
    abortControllerOptions?: Object,
  ): Promise<mixed> {
    const options = {
      method: 'POST',
      headers: {
        Authorization: getCookie('session_id'),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
      ...abortControllerOptions,
    };
    return request(
      `${apiURL}/${organizationId}/${propertyId}/household/${householdId}/unit-transfer/complete-setup`,
      options,
    );
  }

  getActiveTransfer(
    organizationId: string,
    propertyId: string,
    householdId: string,
  ): Promise<mixed> {
    const options = {
      method: 'GET',
      headers: {
        Authorization: getCookie('session_id'),
        'Content-Type': 'application/json',
      },
    };
    return request(
      `${apiURL}/${organizationId}/${propertyId}/household/${householdId}/active-transfer`,
      options,
    );
  }

  updateTransferActions(
    organizationId: string,
    propertyId: string,
    householdId: string,
    actions: Object,
  ): Promise<mixed> {
    const options = {
      method: 'PATCH',
      headers: {
        Authorization: getCookie('session_id'),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(actions),
    };
    return request(
      `${apiURL}/${organizationId}/${propertyId}/household/${householdId}/transfer-actions`,
      options,
    );
  }

  cancelActiveTransfer(
    organizationId: string,
    propertyId: string,
    householdId: string,
  ): Promise<mixed> {
    const options = {
      method: 'POST',
      headers: {
        Authorization: getCookie('session_id'),
        'Content-Type': 'application/json',
      },
    };
    return request(
      `${apiURL}/${organizationId}/${propertyId}/household/${householdId}/unit-transfer/cancel`,
      options,
    );
  }

  completeActiveTransfer(
    organizationId: string,
    propertyId: string,
    householdId: string,
  ): Promise<mixed> {
    const options = {
      method: 'POST',
      headers: {
        Authorization: getCookie('session_id'),
        'Content-Type': 'application/json',
      },
    };
    return request(
      `${apiURL}/${organizationId}/${propertyId}/household/${householdId}/unit-transfer/complete`,
      options,
    );
  }

  editActiveTransfer(
    organizationId: string,
    propertyId: string,
    householdId: string,
    payload: { transferReasonId?: string, transferDate?: string },
  ): Promise<mixed> {
    const options = {
      method: 'POST',
      headers: {
        Authorization: getCookie('session_id'),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    };
    return request(
      `${apiURL}/${organizationId}/${propertyId}/household/${householdId}/unit-transfer/edit`,
      options,
    );
  }

  saveLeaseDataTabResident(
    organizationId: string,
    propertyId: string,
    leaseId: string,
    lease: Object,
  ): any {
    const options = {
      method: 'POST',
      headers: {
        Authorization: getCookie('session_id'),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(lease),
    };
    return request(
      `${apiURL}/${organizationId}/${propertyId}/lease/${leaseId}/lease-resident`,
      options,
    );
  }

  removeLease(leaseId: string, options?: Object): any {
    return del(`/lease/${leaseId}`, options);
  }

  getResidentHousehold(
    residentId: string,
    organizationId: string,
    propertyId: string,
  ): Promise<HouseholdMember[]> {
    const options = {
      method: 'GET',
      headers: {
        Authorization: getCookie('session_id'),
        'Content-Type': 'application/json',
      },
    };

    return request(
      `${apiURL}/${organizationId}/${propertyId}/residents/${residentId}/household`,
      options,
    );
  }
  editResidentHousehold(
    residentId: string,
    payload: any,
    orgId: string,
    propertyId: string,
  ): Promise<any> {
    const options = {
      method: 'PATCH',
      headers: {
        Authorization: getCookie('session_id'),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    };
    return request(
      `${apiURL}/${orgId}/${propertyId}/residents/${residentId}/household`,
      options,
    );
  }

  editPendingApplicants(
    residentId: string,
    payload: any,
    orgId: string,
    propertyId: string,
  ): Promise<any> {
    const options = {
      method: 'PATCH',
      headers: {
        Authorization: getCookie('session_id'),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    };
    return request(
      `${apiURL}/${orgId}/${propertyId}/residents/${residentId}/pending-applicants`,
      options,
    );
  }

  updateResident(
    orgId: string,
    propertyId: string,
    id: string,
    residentInfo: any,
    isAdult?: boolean,
  ): any {
    const resident = isAdult
      ? residentInfo
      : {
          ...omit(['minorsResId'], residentInfo),
          residentId: residentInfo.minorsResId,
        };

    if (
      isAdult &&
      // Minors require these values, Adults do not
      (resident.hasOwnProperty('minorsResId') ||
        resident.hasOwnProperty('applicantId'))
    ) {
      delete resident.minorsResId;
      delete resident.applicantId;
    }

    const options = {
      method: 'POST',
      headers: {
        Authorization: getCookie('session_id'),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(resident),
    };
    const path = isAdult ? 'resident' : 'applicantMinor';
    return request(
      `${apiURL}/${orgId}/${propertyId}/${path}/${id}/update`,
      options,
    );
  }

  getPendingApplicants(
    residentId: string,
    organizationId: string,
    propertyId: string,
  ): Promise<HouseholdMember[]> {
    const options = {
      method: 'GET',
      headers: {
        Authorization: getCookie('session_id'),
        'Content-Type': 'application/json',
      },
    };

    return request(
      `${apiURL}/${organizationId}/${propertyId}/residents/${residentId}/pending-applicants`,
      options,
    );
  }

  /**
   * Saves the Move-In Date. With a Corrected Lease (household-mi-mo-date-changes, ENG-33327) the
   * fields go as multipart/form-data with the file as `correctedLease`, stored in the same
   * transaction as the correction; without one the JSON body is unchanged.
   */
  updateResidentMoveInDate(
    residentId: string,
    organizationId: string,
    propertyId: string,
    modify: Object,
    correctedLease?: ?File,
  ): any {
    let options;
    if (correctedLease) {
      const body = new FormData();
      Object.entries(modify).forEach(([key, value]) => {
        if (value !== null && value !== undefined) {
          body.append(key, String(value));
        }
      });
      body.append('correctedLease', correctedLease);
      // No Content-Type: the browser sets the multipart boundary.
      options = {
        method: 'PUT',
        headers: { Authorization: getCookie('session_id') },
        body,
      };
    } else {
      options = {
        method: 'PUT',
        headers: {
          Authorization: getCookie('session_id'),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(modify),
      };
    }

    return request(
      `${apiURL}/${organizationId}/${propertyId}/residents/${residentId}/update-resident-move-in-date`,
      options,
    );
  }

  /**
   * Dry run of a Move-In Date correction (household-mi-mo-date-changes): what
   * a Save with this date would change. Resolves `{ valid: false, code,
   * message }` when the date breaks a rule, else `{ valid: true, impacts }`.
   * `stayLeaseId` (the move-in lease of a stay) picks the stay to correct;
   * without it the current stay is corrected.
   */
  previewMoveInDateCorrection(
    residentId: string,
    organizationId: string,
    propertyId: string,
    actualMoveInDate: string,
    stayLeaseId: ?string,
    signal?: AbortSignal,
  ): Promise<Object> {
    const options = {
      method: 'POST',
      headers: {
        Authorization: getCookie('session_id'),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        actualMoveInDate,
        ...(stayLeaseId ? { stayLeaseId } : {}),
      }),
      signal,
    };

    return request(
      `${apiURL}/${organizationId}/${propertyId}/residents/${residentId}/move-in-date-correction/preview`,
      options,
    );
  }

  /**
   * The household's stays at this property whose Move-In Date can be
   * corrected (household-mi-mo-date-changes), newest first:
   * `{ stays: CorrectableStay[] }`.
   */
  getMoveInDateCorrectionStays({
    organizationId,
    propertyId,
    residentId,
    signal,
  }: {
    organizationId: string,
    propertyId: string,
    residentId: string,
    signal?: AbortSignal,
  }): Promise<Object> {
    const options = {
      method: 'GET',
      headers: {
        Authorization: getCookie('session_id'),
        'Content-Type': 'application/json',
      },
      signal,
    };

    return request(
      `${apiURL}/${organizationId}/${propertyId}/residents/${residentId}/move-in-date-correction/stays`,
      options,
    );
  }

  updatePriorResidentMoveOutDate(
    residentId: string,
    organizationId: string,
    propertyId: string,
    modify: Object,
  ): any {
    const options = {
      method: 'PUT',
      headers: {
        Authorization: getCookie('session_id'),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(modify),
    };

    return request(
      `${apiURL}/${organizationId}/${propertyId}/prior-residents/${residentId}/update-move-out-date`,
      options,
    );
  }

  /**
   * Dry run of a prior resident's Move-Out Date correction (household-mi-mo-date-changes): what a
   * Save with this date would change to the final stay. Resolves `{ valid: false, code, message }`
   * when the date breaks a rule, else `{ valid: true, impacts }`.
   */
  previewMoveOutDateCorrection(
    residentId: string,
    organizationId: string,
    propertyId: string,
    moveOutDate: string,
    signal?: AbortSignal,
  ): Promise<Object> {
    return post(
      `/${organizationId}/${propertyId}/prior-residents/${residentId}/move-out-date-correction/preview`,
      JSON.stringify({ moveOutDate }),
      { signal },
    );
  }

  updateResidentPropertyMoveInDate(
    residentId: string,
    organizationId: string,
    propertyId: string,
    modify: Object,
  ): any {
    const options = {
      method: 'PUT',
      headers: {
        Authorization: getCookie('session_id'),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(modify),
    };

    return request(
      `${apiURL}/${organizationId}/${propertyId}/residents/${residentId}/update-resident-property-move-in-date`,
      options,
    );
  }

  getLatestHouseholdHistory(
    residentId: string,
    organizationId: string,
    propertyId: string,
  ): any {
    const options = {
      headers: {
        Authorization: getCookie('session_id'),
        'Content-Type': 'application/json',
      },
    };

    return get(
      `/${organizationId}/${propertyId}/residents/${residentId}/household-history`,
      options,
    );
  }

  getHouseholdLeasesByResidentId(
    organizationId: string,
    propertyId: string,
    residentId: string,
    options?: Object,
  ): any {
    return get(
      `/${organizationId}/${propertyId}/residents/${residentId}/leases`,
      options,
    );
  }

  getFirstUnitMoveInAfterPriorResident(
    organizationId: string,
    propertyId: string,
    residentId: string,
  ): any {
    const options = {
      method: 'GET',
      headers: {
        Authorization: getCookie('session_id'),
        'Content-Type': 'application/json',
      },
    };

    return request(
      `${apiURL}/${organizationId}/${propertyId}/prior-residents/${residentId}/next-unit-move-in`,
      options,
    );
  }

  getAllResidents(organizationId: string, propertyId: string): any {
    return get(
      `/${organizationId}/${propertyId}/residents?financialResponsable=true&nameUnitAndBalanceOnly=true`,
    );
  }
  /**
   *
   *
   * @param {string} organizationId
   * @param {string} propertyId
   * @param {string} residentId
   * @param {boolean} [checkForGrossRents=false]
   * @return {(Promise<Object>|Promise<boolean>)} When checkForGrossRents = true then Promise<{hasNonGrossRentOpenCert: boolean, hasGrossRentOpenCert: boolean}> otherwise Promise<boolean>
   * @memberof ResidentService
   */
  getHouseholdHasOpenCerts(
    organizationId: string,
    propertyId: string,
    residentId: string,
    checkForGrossRents?: boolean = false,
  ): Promise<boolean> {
    const options = {
      headers: {
        Authorization: getCookie('session_id'),
        'Content-Type': 'application/json',
      },
    };

    return get(
      `/${organizationId}/${propertyId}/resident/${residentId}/has-${
        checkForGrossRents ? 'any-' : ''
      }open-certs`,
      options,
    );
  }

  /**
   * Whether the household has an open certification, and whether any of them
   * is locked.
   *
   * @param {string} organizationId
   * @param {string} propertyId
   * @param {string} residentId
   * @return {Promise<{hasOpenCerts: boolean, hasLockedOpenCert: boolean}>}
   * @memberof ResidentService
   */
  getOpenCertsLockInfo(
    organizationId: string,
    propertyId: string,
    residentId: string,
  ): Promise<{ hasOpenCerts: boolean, hasLockedOpenCert: boolean }> {
    return get(
      `/${organizationId}/${propertyId}/resident/${residentId}/open-certs-lock-info`,
    );
  }

  /**
   * Checks if household has open certifications excluding MOVE_OUT type.
   * Used for move-out validation when "Death of Sole Family Member" scenario
   * allows processing with open MOVE_OUT cert.
   *
   * @param {string} organizationId
   * @param {string} propertyId
   * @param {string} residentId
   * @return {Promise<{hasNonMoveOutOpenCert: boolean}>}
   * @memberof ResidentService
   */
  getHouseholdHasNonMoveOutOpenCerts(
    organizationId: string,
    propertyId: string,
    residentId: string,
  ): Promise<{ hasNonMoveOutOpenCert: boolean }> {
    const options = {
      headers: {
        Authorization: getCookie('session_id'),
        'Content-Type': 'application/json',
      },
    };

    return get(
      `/${organizationId}/${propertyId}/resident/${residentId}/has-non-move-out-open-certs`,
      options,
    );
  }

  /**
   * Will return the INCOME & ASSETS CHECKLISTS of the previous MOST RECENT COMPLETED AFFORDABLE QUALIFICATION
   * related to the person (household member) identified by the residentId
   * ---
   * @param {*} organizationId
   * @param {*} propertyId
   * @param {*} residentId
   * @param {*} applicantId
   * @returns the income and assets lists
   */
  getResidentIncomeAndAssetsDataFromMostRecentCompletedAQ(
    propertyId: string,
    residentId: string,
    applicantId: string,
    householdId: string,
    affordableProgramName: string,
  ): Promise<Array<Object>> {
    const options = {
      headers: {
        Authorization: getCookie('session_id'),
        'Content-Type': 'application/json',
      },
    };

    const queryString = `affordableProgramName=${affordableProgramName}`;

    return get(
      // eslint-disable-next-line max-len
      `/${propertyId}/resident/${residentId}/${applicantId}/${householdId}/income-and-assets-data-from-most-recent-completed-aq?${queryString}`,
      options,
    );
  }

  getResidentScreenings(
    organizationId: string,
    propertyId: string,
    residentId: string,
    householdId: string,
    options?: Object,
  ): any {
    void organizationId;
    void propertyId;
    void residentId;
    return get(
      `/screening-service/screenings/households/${householdId}/history`,
      options,
    );
  }
}
